import { Button, Container, ImageInput, Input } from "components/atoms";
import { FormInput } from "components/molecules";
import DataWrapper from "components/molecules/data-wrapper/DataWrapper";
import { useFormikContext } from "formik";
import { IFormikContextType } from "hooks/useFormikField";
import { useTranslation } from "react-i18next";
import { PiHeadphones, PiImage } from "react-icons/pi";
import { useLocation, useNavigate } from "react-router-dom";
import { useGenerateEan } from "services/codesDistribuator/ean";
import { useGetGenres, useGetLanguages } from "services/enums";
import {
  DIGITAL_PRODUCT_TYPE,
  RELEASE_TYPE,
} from "services/music-product-digital/types/enums";
import { IMusicProductPhysicalForm } from "services/music-product-physical/types/form";
import {
  getYearsSelectOptions,
  useEnumToSelectOptions,
} from "utils/components/inputs/select";
import { getLanguageSelectOptions } from "utils/global/language.utils";
import { onErrorShowAllErrorsToast } from "utils/service/errorHandler";

const MusicProductPhysicalData = () => {
  const { t } = useTranslation();
  const { getSelectOptions } = useEnumToSelectOptions();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const languages = useGetLanguages();

  const generateEan = useGenerateEan();

  const genres = useGetGenres();
  const {
    values,
    setFieldValue,
    status,
    initialValues,
  }: IFormikContextType<IMusicProductPhysicalForm> =
    useFormikContext<IMusicProductPhysicalForm>();

  return (
    <DataWrapper>
      <Container className=" self-start  ">
        <Container.Header
          title={
            <>
              <PiImage /> {t("input.label.image")}
            </>
          }
        />
        <Container.Content className="w-full items-center">
          <div className="h-[200px] w-[200px]">
            <ImageInput
              fullResolutionUrl={values.productImageLink ?? undefined}
              previewUrl={values.productImagePreviewLink ?? undefined}
              editable={!status?.displayMode}
              onChange={(file) => setFieldValue("file", file)}
              onDelete={
                initialValues.productImageLink
                  ? (action) => {
                      setFieldValue("productImageLink", "");
                      setFieldValue("productImagePreviewLink", "");
                      setFieldValue("file", undefined);
                      action.close();
                    }
                  : undefined
              }
            />
          </div>
        </Container.Content>
      </Container>
      <Container>
        <Container.Header
          title={
            <>
              <PiHeadphones /> {t("artist.tabs.data")}
            </>
          }
        />

        <Container.Content>
          <FormInput.Select
            name="productType"
            label={t("input.label.productType")}
            options={getSelectOptions(
              DIGITAL_PRODUCT_TYPE,
              "enum.DIGITAL_PRODUCT_TYPE",
            )}
            isClearable={false}
            onChange={() => {
              setFieldValue("releaseType", RELEASE_TYPE.single);
              setFieldValue("settlementInfo.perTrackSettlement", false);
              if (pathname.includes("tracks")) {
                navigate(pathname.replace("tracks", "video"));
              }
              if (pathname.includes("video")) {
                navigate(pathname.replace("video", "tracks"));
              }
            }}
          />
          {/* <FormInput.Select
            name="releaseType"
            label={t("input.label.releaseType")}
            options={getSelectOptions(RELEASE_TYPE, "enum.RELEASE_TYPE")}
            isDisabled={values.productType === DIGITAL_PRODUCT_TYPE.video}
          /> */}

          <FormInput.Text
            type="date"
            name="releaseDate"
            label={t("input.label.releaseDate")}
          />

          <FormInput.Select
            name="mainTitle.language"
            label={t("input.label.language")}
            options={getLanguageSelectOptions(languages.data, [
              ...values.otherTitles,
              values.mainTitle,
            ])}
            value={{
              value: values.mainTitle.language,
              label: t(`enum.LANGUAGE.${values.mainTitle.language}`),
            }}
          />
          <FormInput.Select
            name="genre.id"
            label={t("input.label.genre")}
            options={
              genres.data?.map((genre) => ({
                value: genre.id,
                label: genre.name,
              })) ?? []
            }
            isLoading={genres.isLoading}
            value={{
              value: values.genre.id,
              label: values.genre.name,
            }}
            onChange={(option) => {
              if (option) {
                setFieldValue("genre", {
                  id: option.value,
                  name: option.label,
                });
              }
            }}
          />
          <Input.ComplexWrapper
            sizes={
              !status?.displayMode && !(initialValues.ean || values.ean)
                ? [69, 30]
                : []
            }
            className="w-full"
          >
            <FormInput.Text
              name="ean"
              label={t("input.label.ean")}
              type="number"
            />

            {!status?.displayMode && !(initialValues.ean || values.ean) && (
              <Button
                onClick={async () =>
                  await generateEan.mutateAsync(undefined, {
                    onSuccess: (ean) => setFieldValue("ean", ean.value),
                    onError: (error) => onErrorShowAllErrorsToast(error),
                  })
                }
              >
                <Button.Text text={t("button.generate")} />
              </Button>
            )}
          </Input.ComplexWrapper>
          <Input.ComplexWrapper sizes={[40, 60]} className="w-full">
            <FormInput.Select
              name="pLine.year"
              label={`${t("input.label.year")}`}
              options={getYearsSelectOptions()}
              isCreatable
            />
            <FormInput.Select
              name="pLine.name"
              label={`${t("input.label.pline")}`}
              placeholder=""
              // options={publishers.data}
              isCreatable
              titleEnabled
            />
          </Input.ComplexWrapper>
          <Input.ComplexWrapper sizes={[40, 60]} className="w-full">
            <FormInput.Select
              name="cLine.year"
              label={`${t("input.label.year")}`}
              placeholder=""
              options={getYearsSelectOptions()}
              isCreatable
            />
            <FormInput.Select
              name="cLine.name"
              label={`${t("input.label.cline")}`}
              placeholder=""
              // options={publishers.data}
              isCreatable
              titleEnabled
            />
          </Input.ComplexWrapper>
        </Container.Content>
      </Container>
    </DataWrapper>
  );
};

export default MusicProductPhysicalData;
